import type {ReactNode, RefObject} from 'react';
import type {TShow} from '../../../hooks/useShow';
import type {TXHR} from '../../../assets/constants/constansts.type';
import type {TemplateStructure} from '../../whatsAppTemplateEditor/types/templateStructure';

export type PendingChatAttachment = {
    file: File;
    previewUrl: string;
};

export type ChatMessageAttachment = {
    name: string;
    kind: 'image' | 'file';
    previewUrl: string;
};

export type UserChatMessage = {
    role: 'user';
    text: string;
    id?: string;
    attachment?: ChatMessageAttachment;
};
export type AssistantChatMessage = {
    role: 'assistant';
    text: string;
    id?: string;
    parameterValues?: TemplateStructure;
};
export type ChatMessage = UserChatMessage | AssistantChatMessage;
export type AssistantDraftPayload = AssistantChatMessage;

export interface ChatAskOption {
    label: string;
    value: string;
}

export interface ChatAskPrompt {
    id?: string;
    question: string;
    options: ChatAskOption[];
}

export interface DraftResult {
    text: string;
    parameterValues?: TemplateStructure;
    ask?: ChatAskPrompt;
}

export type AISubmitHandler = (
    prompt: string,
    history: ChatMessage[],
    sessionId: string,
    attachment?: PendingChatAttachment | null,
) => Promise<DraftResult | null>;

export function mapChatMessagesToApiHistory(messages: ChatMessage[]): ChatMessage[] {
    return messages.map(message => {
        if(message.role === 'user') {
            // previewUrl is a local blob: URL and is deliberately not sent to the server.
            // Empty text + image → keep a short placeholder so history validation accepts it.
            const hasImage = message.attachment?.kind === 'image';
            const text = message.text?.trim()
                || (hasImage ? '[attached image]' : '');
            return {
                role: 'user',
                text,
                ...(message.attachment ? {attachment: {name: message.attachment.name, kind: message.attachment.kind, previewUrl: ''}} : {}),
            };
        }
        return {
            role: 'assistant',
            text: message.text,
            ...(message.parameterValues ? {parameterValues: message.parameterValues} : {}),
        };
    });
}

export interface AIMessageGeneratorEmptyStateProps {
    onSuggestionClick: (text: string, index: number) => void;
}

export interface AIMessageGeneratorUserMessageProps {
    message: UserChatMessage;
    index: number;
    onEditMessage?: (index: number, newText: string) => void;
    draftLoading?: boolean;
}

export interface SubmitPromptOptions {
    text?: string;
    /** Shown in the chat bubble when different from `text` (e.g. ask option label vs value). */
    displayText?: string;
    attachment?: PendingChatAttachment | null;
}

export interface AIMessageGeneratorPromptAreaProps {
    promptText: string;
    placeholder: string;
    draftLoading: boolean;
    disableSubmit: boolean;
    promptTextareaRef: RefObject<HTMLTextAreaElement>;
    onPromptChange: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
    onPromptKeyDown: (e: React.KeyboardEvent<HTMLTextAreaElement>) => void;
    onSubmitPrompt: (options?: SubmitPromptOptions) => void;
    onPendingAttachmentChange?: (hasAttachment: boolean) => void;
}

export interface AIMessageGeneratorProps {
    show: TShow;
    title: string;
    placeholder: string;
    newChatLabel: string;
    renderEmptyState?: (props: AIMessageGeneratorEmptyStateProps) => ReactNode;
    renderPromptArea?: (props: AIMessageGeneratorPromptAreaProps) => ReactNode;
    renderUserMessage?: (props: AIMessageGeneratorUserMessageProps) => ReactNode;
    onReset?: () => void;
    submit: {
        label: string;
        onClick: AISubmitHandler;
        xhrState?: TXHR;
    };
    /** Omit to hide the approve action entirely (chat-only mode). */
    approve?: {
        label: string;
        onClick: (draft: AssistantDraftPayload) => Promise<void>;
        xhrState?: TXHR;
    };
    errorMessage: string;
    suggestions: string[];
    analytics?: {
        contentId: string | number;
        sourceTypeId: string;
        context?: Record<string, unknown>;
    };
    container?: 'sidePopup' | 'innerPop';
    /** When false, backdrop/outside click do not close — only the header close control. Default true. */
    closeOnOutsideClick?: boolean;
    /** Hide the SidePopup backdrop (keeps the panel usable over forms). */
    hideBackdrop?: boolean;
    /** When false, closing the panel keeps the conversation for the next open. Default true. */
    resetOnClose?: boolean;
    dataCy?: string;
}
